import React, { useCallback, useEffect, useState } from 'react';
import { push } from 'connected-react-router';
import { Flex, Text, TextButton } from '@zillow/constellation';
import * as S from './styles';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { useRentalMarketFilters } from 'app/shared/hooks/useFilters';
import dateUtils from 'app/shared/utils/dateUtils';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { decodeResourceIdFromUrl } from 'app/shared/utils/urlUtils';
import { getTopMarkets } from 'app/shared/flux/actions/AreaActions';
import { resetFilter } from 'app/shared/flux/actions/FilterActions';
import StringConstants from 'app/shared/constants/StringConstants';
import { decorate } from 'app/shared/models/TopMarkets';
import { trackEvent } from 'app/shared/utils/analytics/trackEvent';
import { gaEvents, RMP_CATEGORY } from 'app/shared/constants/AnalyticsConstants';
import { isEmpty as isMarketStatsEmpty } from 'app/shared/utils/marketStatsUtils';

import PageHead from './PageHead';
import SEOFooter from './SEOFooter';
import CompareRent from 'app/shared/components/CompareRent';
import FilterTray from 'app/shared/components/FilterTray';
import { PropertyTypes } from 'app/shared/types/PropertyTypes';
import { Bedrooms } from 'app/shared/types/Bedrooms';
import MarketFAQ from 'app/shared/components/MarketFAQ';
import MarketSummary from 'app/shared/components/MarketSummary';
import MarketTemperature from 'app/shared/components/MarketTemperature';
import MarketUpsell from 'app/shared/components/MarketUpsell';
import RentHistogram from 'app/shared/components/RentHistogram';
import RentLineGraph from 'app/shared/components/RentLineGraph';
import ResponsiveHeading from 'app/shared/StyledComponents/ResponsiveText';
import TopMarkets from 'app/shared/components/TopMarkets';
import UpsellCarouselModule from 'app/shared/modules/UpsellCarouselModule';

import type { State } from 'app/shared/types/State';
import { augmentError, formatForLog } from 'app/shared/utils/errors';
import { AreaType } from 'app/shared/types/AreaType';
import NearbyAreaTrends from 'app/shared/components/NearbyAreaTrends';

export const DESCRIPTION_MATRIX = {
    [Bedrooms.ALL]: {
        [PropertyTypes.ALL]: 'all bedrooms and all property types',
        [PropertyTypes.APTSANDCONDOS]: 'apartments',
        [PropertyTypes.HOUSE]: 'houses',
        [PropertyTypes.TOWNHOUSE]: 'townhouses'
    },
    [Bedrooms.STUDIO]: {
        [PropertyTypes.ALL]: 'studio properties',
        [PropertyTypes.APTSANDCONDOS]: 'studio apartments',
        [PropertyTypes.HOUSE]: 'studio houses',
        [PropertyTypes.TOWNHOUSE]: 'studio townhouses'
    },
    [Bedrooms.ONE]: {
        [PropertyTypes.ALL]: '1 bedroom properties',
        [PropertyTypes.APTSANDCONDOS]: '1 bedroom apartments',
        [PropertyTypes.HOUSE]: '1 bedroom houses',
        [PropertyTypes.TOWNHOUSE]: '1 bedroom townhouses'
    },
    [Bedrooms.TWO]: {
        [PropertyTypes.ALL]: '2 bedroom properties',
        [PropertyTypes.APTSANDCONDOS]: '2 bedroom apartments',
        [PropertyTypes.HOUSE]: '2 bedroom houses',
        [PropertyTypes.TOWNHOUSE]: '2 bedroom townhouses'
    },
    [Bedrooms.THREE]: {
        [PropertyTypes.ALL]: '3 bedroom properties',
        [PropertyTypes.APTSANDCONDOS]: '3 bedroom apartments',
        [PropertyTypes.HOUSE]: '3 bedroom houses',
        [PropertyTypes.TOWNHOUSE]: '3 bedroom townhouses'
    },
    [Bedrooms.FOURPLUS]: {
        [PropertyTypes.ALL]: '4+ bedroom properties',
        [PropertyTypes.APTSANDCONDOS]: '4+ bedroom apartments',
        [PropertyTypes.HOUSE]: '4+ bedroom houses',
        [PropertyTypes.TOWNHOUSE]: '4+ bedroom townhouses'
    }
};

const logger = log.child('pages/MarketStatsResultsPage');

const MarketStatsResultsPage = () => {
    const dispatch = useDispatch();
    const { isMobile, screenWidth } = useSelector((state: State) => state.app.device);
    const marketStats = useSelector((state: State) => state.marketStats);
    const faqParams = useSelector((state: State) => state.unfilteredMarketStats);
    const topMarkets = useSelector((state: State) => state.topMarkets);
    const pathname = useSelector((state: State) => state.router.location.pathname);
    const query = useSelector((state: State) => state.router.location.query);
    const queryString = useSelector((state: State) => state.router.location.search);
    const filter = useSelector((state: State) => state.filter);
    const { bedrooms, setBedrooms, propertyTypes, setPropertyTypes } = useRentalMarketFilters(filter, dispatch);
    const [loading, setLoading] = useState(false);
    const chooseAnotherArea: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
        async (e) => {
            e.preventDefault();
            if (!(propertyTypes === PropertyTypes.ALL && bedrooms === Bedrooms.ALL)) {
                await dispatch(getTopMarkets({}))
                    // @ts-ignore
                    .catch((err) => {
                        augmentError(err, {
                            root: 'pages/MarketStatsResultsPage#chooseAnotherArea'
                        });

                        logger.error(formatForLog(err), 'Market Stats Results Page: Error fetching Top Markets (CSR)');
                    });
                dispatch(resetFilter());
            }
            trackEvent(gaEvents.SEARCH_OTHER_AREA);
            dispatch(push(pathsManifest.MarketTrendsPage + '/'));
        },
        [bedrooms, propertyTypes, dispatch]
    );
    useEffect(() => {
        if (!hasData) {
            if (areaName) {
                trackEvent({ ...gaEvents.AREA_HAS_NO_DATA, label: resourceId });
            } else {
                trackEvent({ ...gaEvents.AREA_DOES_NOT_EXIST, label: resourceId });
            }
        }
    }, []);

    const topRentalMarkets = topMarkets.topRentalMarkets.map((market) => decorate(market, queryString));
    const {
        areaName,
        areaType,
        date,
        zillowRentalsSrpUrl,
        medianRent,
        minRent,
        maxRent,
        monthlyChange,
        yearlyChange,
        avgDaysOnMarket,
        availableRentals,
        temperature,
        medianNationalRent,
        rentCounts,
        curYearRentPrices,
        prevYearRentPrices,
        nearbyAreas,
        zipcodesInCity,
        nearbyAreaTrends
    } = marketStats;
    const zipcodes = areaType === AreaType.ZIP ? [] : zipcodesInCity;
    const hasData = !isMarketStatsEmpty(marketStats);
    const hasMarketSummary = !!(medianRent || monthlyChange || yearlyChange || avgDaysOnMarket || availableRentals);
    const resourceId = decodeResourceIdFromUrl(pathname);
    const displayDate = dateUtils.displayDate(date);
    const isSmallScreen = isMobile || screenWidth === 'md';

    const chooseAnotherAreaButton = (
        <TextButton
            as="a"
            href={pathsManifest.MarketTrendsPage + '/'}
            onClick={chooseAnotherArea}
            fontType="bodySmallHeading"
            marginBottom="sm"
            disabled={loading}
            style={{ height: 24 }}
        >
            Choose another area
        </TextButton>
    );

    const navButtons = (
        <>
            {chooseAnotherAreaButton}
            <div>
                <FilterTray
                    bedrooms={bedrooms}
                    setBedrooms={setBedrooms}
                    propertyTypes={propertyTypes}
                    setPropertyTypes={setPropertyTypes}
                    loading={loading}
                    setLoading={setLoading}
                    isMobile={isMobile}
                />
                {displayDate && (
                    <S.LastUpdatedDate as="p" fontType="finePrint" marginBottom="sm" alignLeft={isSmallScreen}>
                        Last updated {displayDate}
                    </S.LastUpdatedDate>
                )}
            </div>
        </>
    );

    return (
        <>
            <PageHead
                areaName={areaName}
                hasData={hasData}
                faqParams={faqParams}
                canonicalURL={isEmpty(query) ? null : `${StringConstants.ZILLOW_URL}${pathname}`}
            />
            <S.Container>
                <S.Content loading={loading}>
                    {hasData && typeof areaName === 'string' ? (
                        <>
                            <ResponsiveHeading level="2" as="h1" fontFamily="serif" fontType="h2">
                                {areaName} Rental Market
                            </ResponsiveHeading>

                            {isSmallScreen ? (
                                navButtons
                            ) : (
                                <Flex display="flex" flexDirection="row" justifyContent="space-between" flexWrap="wrap">
                                    {navButtons}
                                </Flex>
                            )}
                            {hasMarketSummary && (
                                <MarketSummary
                                    market={areaName}
                                    filterDescription={DESCRIPTION_MATRIX[bedrooms][propertyTypes]}
                                    medianRent={medianRent}
                                    monthlyChange={monthlyChange}
                                    yearlyChange={yearlyChange}
                                    avgDaysOnMarket={avgDaysOnMarket}
                                    availableRentals={availableRentals}
                                    isSmallScreen={isSmallScreen}
                                />
                            )}
                            <Flex
                                display="flex"
                                flexDirection="row"
                                flexWrap="wrap"
                                // @ts-ignore
                                gap="md"
                                marginTop="md"
                            >
                                {rentCounts.length > 0 && (
                                    <RentHistogram
                                        rentCounts={rentCounts}
                                        minRent={minRent}
                                        maxRent={maxRent}
                                        filterDescription={DESCRIPTION_MATRIX[bedrooms][propertyTypes]}
                                        isSmallScreen={isSmallScreen}
                                    />
                                )}
                                {(curYearRentPrices.length > 0 || prevYearRentPrices.length > 0) && (
                                    <RentLineGraph
                                        prevYear={prevYearRentPrices}
                                        curYear={curYearRentPrices}
                                        yearlyChange={yearlyChange}
                                        isSmallScreen={isSmallScreen}
                                    />
                                )}
                                {temperature !== null && (
                                    <MarketTemperature temp={temperature} isSmallScreen={isSmallScreen} />
                                )}
                                {medianRent !== null && (
                                    <CompareRent
                                        market={{ name: areaName, medianRent, resourceId }}
                                        medianNationalRent={medianNationalRent}
                                        isSmallScreen={isSmallScreen}
                                        isMobile={isMobile}
                                    />
                                )}
                            </Flex>
                            {zillowRentalsSrpUrl && (
                                <MarketUpsell
                                    name={areaName}
                                    isSmallScreen={isSmallScreen}
                                    href={zillowRentalsSrpUrl}
                                />
                            )}
                            <UpsellCarouselModule gaTrackingCategory={RMP_CATEGORY} marginTop="lg" />
                            {nearbyAreaTrends.length ? (
                                <NearbyAreaTrends
                                    nearbyAreaTrends={nearbyAreaTrends}
                                    isSmallScreen={isSmallScreen}
                                    isMobile={isMobile}
                                />
                            ) : null}
                            <MarketFAQ marketStats={faqParams} isMobile={isMobile} />
                            {nearbyAreas.length || zipcodes.length ? (
                                <SEOFooter areaName={areaName} zipcodesInCity={zipcodes} nearbyAreas={nearbyAreas} />
                            ) : null}
                        </>
                    ) : (
                        <>
                            <ResponsiveHeading level="2" as="h1" fontFamily="serif" fontType="h2">
                                We&#39;re sorry!
                            </ResponsiveHeading>
                            {areaName === null ? (
                                <Text as="p" marginTop="md">
                                    We can&#39;t seem to find any data that matches your search. Choose another area or
                                    browse top rental markets below.
                                </Text>
                            ) : (
                                <Text as="p" marginTop="md">
                                    We ca&#39;t seem to find any data that matches your search for{' '}
                                    <strong>
                                        <i>{areaName}</i>
                                    </strong>
                                    . Choose another area or browse top rental markets below.
                                </Text>
                            )}
                            {chooseAnotherAreaButton}
                            <TopMarkets markets={topRentalMarkets} isSmallScreen={isSmallScreen} isMobile={isMobile} />
                        </>
                    )}
                </S.Content>
            </S.Container>
        </>
    );
};

export default MarketStatsResultsPage;
